import React, { useState, useEffect, useMemo } from 'react';
import { Tree, Badge, Tooltip } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import './MonitoringTree.css';

const MonitoringTree = ({
  nodes,
  monitoringStates = {},
  onNodeClick,
  showStatus = true,
  autoExpandAll = false
}) => {
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);

  // Auto-expand all nodes if requested
  useEffect(() => {
    if (autoExpandAll && nodes && nodes.length > 0) {
      const allKeys = [];
      const collectKeys = (nodeList) => {
        nodeList.forEach(node => {
          allKeys.push(node.id.toString());
          if (node.children && node.children.length > 0) {
            collectKeys(node.children);
          }
        });
      };
      collectKeys(nodes);
      setExpandedKeys(allKeys);
    }
  }, [autoExpandAll, nodes]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'green':
      case 'normal':
        return 'success';
      case 'yellow':
      case 'warning':
        return 'warning';
      case 'red':
      case 'critical':
        return 'error';
      case 'unknown':
        return 'default';
      default:
        return 'default';
    }
  };

  const getNodeStatus = (nodeId) => {
    return monitoringStates[nodeId] || null;
  };

  const buildTreeData = (node) => {
    if (!node) return null;

    const nodeStatus = getNodeStatus(node.id);
    const status = nodeStatus?.status;
    const statusMessage = nodeStatus?.status_message;

    const titleContent = (
      <div className="monitoring-tree-node">
        {showStatus && status && (
          <Badge status={getStatusColor(status)} />
        )}
        <span className="node-name">{node.display_name || node.name}</span>
        {statusMessage && (
          <Tooltip title={statusMessage}>
            <span className="node-status-message">- {statusMessage}</span>
          </Tooltip>
        )}
      </div>
    );

    const treeNode = {
      key: node.id.toString(),
      title: titleContent,
      data: { ...node, status, statusMessage }
    };

    if (node.children && node.children.length > 0) {
      treeNode.children = node.children.map(buildTreeData).filter(Boolean);
    }

    return treeNode;
  };

  const handleSelect = (selectedKeys, info) => {
    if (selectedKeys.length > 0 && onNodeClick) {
      setSelectedKeys(selectedKeys);
      onNodeClick(info.node.data);
    }
  };

  const handleExpand = (expandedKeys) => {
    setExpandedKeys(expandedKeys);
  };

  const treeData = useMemo(() => {
    if (!nodes || nodes.length === 0) return [];
    return nodes.map(buildTreeData).filter(Boolean);
  }, [nodes, monitoringStates, showStatus]);

  if (!nodes || nodes.length === 0) {
    return (
      <div className="monitoring-tree-empty">
        <p>No monitoring hierarchy configured for this lab.</p>
      </div>
    );
  }

  return (
    <div className="monitoring-tree-container">
      <Tree
        showLine
        switcherIcon={<DownOutlined />}
        treeData={treeData}
        expandedKeys={expandedKeys}
        selectedKeys={selectedKeys}
        onExpand={handleExpand}
        onSelect={handleSelect}
        className="monitoring-tree"
      />
    </div>
  );
};

MonitoringTree.propTypes = {
  nodes: PropTypes.arrayOf(PropTypes.object),
  monitoringStates: PropTypes.object,
  onNodeClick: PropTypes.func,
  showStatus: PropTypes.bool,
  autoExpandAll: PropTypes.bool
};

export default MonitoringTree;
